import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

function Card({ label, value, accent }) {
  return <div className="eyw-card p-5"><div className={`text-2xl font-serif ${accent || "text-[#144F75]"}`}>{value}</div><p className="text-xs text-[#4A5D6B] mt-1">{label}</p></div>;
}

export default function POSReports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["reports", from, to],
    queryFn: () => api.get(`/reports/summary?dfrom=${from}&dto=${to}`).then((r) => r.data),
  });
  const byPerson = useQuery({
    queryKey: ["reports-by-person", from, to],
    queryFn: () => api.get(`/reports/by-person?dfrom=${from}&dto=${to}`).then((r) => r.data),
  });

  const downloadByPerson = async () => {
    setDownloading("person");
    try {
      const res = await api.get(`/reports/by-person.csv?dfrom=${from}&dto=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "eyw-sales-by-person.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "We couldn't prepare that export. Please try again."); }
    finally { setDownloading(""); }
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/reports/sales.${fmt}?dfrom=${from}&dto=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `eyw-sales.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "We couldn't prepare that export. Please try again."); }
    finally { setDownloading(""); }
  };

  const t = data?.totals;
  return (
    <div data-testid="pos-reports">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div><label className="text-xs text-[#4A5D6B] block">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="eyw-input" /></div>
        <div><label className="text-xs text-[#4A5D6B] block">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="eyw-input" /></div>
        <button onClick={() => { refetch(); byPerson.refetch(); }} data-testid="report-run" className="eyw-btn !py-2 text-sm">{isFetching && <Loader2 className="animate-spin" size={15} />} Run</button>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => download("csv")} disabled={downloading} data-testid="export-csv" className="eyw-btn-ghost !py-2 text-sm"><FileDown size={15} /> CSV</button>
          <button onClick={() => download("xlsx")} disabled={downloading} data-testid="export-xlsx" className="eyw-btn-ghost !py-2 text-sm"><FileSpreadsheet size={15} /> Excel</button>
          <button onClick={() => download("pdf")} disabled={downloading} data-testid="export-pdf" className="eyw-btn-ghost !py-2 text-sm"><FileText size={15} /> PDF</button>
        </div>
      </div>

      {isLoading || !t ? <div className="py-16 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8" data-testid="report-totals">
            <Card label="Gross sales" value={`${money(Number(t.gross_sales || 0))}`} />
            <Card label="Net sales" value={`${money(Number(t.net_sales || 0))}`} />
            <Card label="Discounts" value={`${money(Number(t.discounts || 0))}`} accent="text-[#B5794E]" />
            <Card label="Tips" value={`${money(Number(t.tips || 0))}`} />
            <Card label="Refunds" value={`${money(Number(t.refunds || 0))}`} accent="text-[#B5794E]" />
            <Card label="Pkg redemptions" value={`${t.package_redemptions || 0}${t.package_redemption_value ? ` (${money(Number(t.package_redemption_value))})` : ""}`} accent="text-[#5C8A72]" />
            <Card label="Transactions" value={t.transactions} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="eyw-card p-6">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Sales by category</h3>
              {Object.keys(data.by_category).length ? Object.entries(data.by_category).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1 border-b border-[#E5E0D8] last:border-0"><span className="capitalize text-[#1A242B]">{k}</span><span className="text-[#144F75]">{money(v)}</span></div>
              )) : <p className="text-sm text-[#4A5D6B]">No sales in range.</p>}
            </div>
            <div className="eyw-card p-6">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Top products (units)</h3>
              {Object.keys(data.top_products).length ? Object.entries(data.top_products).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1 border-b border-[#E5E0D8] last:border-0"><span className="text-[#1A242B] truncate">{k}</span><span className="text-[#4A5D6B]">{v}</span></div>
              )) : <p className="text-sm text-[#4A5D6B]">No product sales in range.</p>}
            </div>
          </div>

          <div className="eyw-card p-6 mt-6" data-testid="report-by-person">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-lg text-[#1A242B]">Sales by person <span className="text-xs font-sans text-[#8FA89B]">(enhancers & vendors — from revenue splits)</span></h3>
              <button onClick={downloadByPerson} disabled={downloading === "person"} data-testid="export-by-person" className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><FileDown size={13} /> CSV</button>
            </div>
            {byPerson.isLoading ? <p className="text-sm text-[#4A5D6B]"><Loader2 className="animate-spin inline" size={14} /> Loading…</p> :
              (byPerson.data?.rows || []).length === 0 ? <p className="text-sm text-[#4A5D6B]">No attributed sales in range.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[#4A5D6B] text-xs uppercase tracking-wide">
                      <tr className="border-b border-[#E5E0D8]"><th className="text-left py-2">Person</th><th className="text-left py-2">Type</th><th className="text-right py-2">Txns</th><th className="text-right py-2">Gross</th><th className="text-right py-2">Commission</th><th className="text-right py-2">Ecosystem</th></tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E0D8]">
                      {byPerson.data.rows.map((r) => (
                        <tr key={r.person_id || "house"} data-testid={`by-person-row-${r.person_id || "house"}`}>
                          <td className="py-2 text-[#1A242B]">{r.name}</td>
                          <td className="py-2 text-[#8FA89B] capitalize">{r.type || (r.person_id ? "" : "house")}</td>
                          <td className="py-2 text-right text-[#4A5D6B]">{r.transactions}</td>
                          <td className="py-2 text-right text-[#144F75] font-medium">{money(r.gross)}</td>
                          <td className="py-2 text-right text-[#5C8A72] font-medium">{money(r.commission)}</td>
                          <td className="py-2 text-right text-[#8FA89B]">{money(r.ecosystem_share)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[#E5E0D8] font-medium">
                        <td className="py-2 text-[#1A242B]">Total</td><td />
                        <td className="py-2 text-right">{byPerson.data.totals.transactions}</td>
                        <td className="py-2 text-right text-[#144F75]">{money(byPerson.data.totals.gross)}</td>
                        <td className="py-2 text-right text-[#5C8A72]">{money(byPerson.data.totals.commission)}</td><td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );
}
